import { CalculatorLink } from '@/components/blog/CalculatorLink'
import { BlogLink } from '@/components/blog/BlogLink'

export default function Article() {
  return (
    <>
      <p>
        Dwie osoby, ten sam przelew na konto — 15 000 zł miesięcznie. Jedna pracuje na etacie,
        druga prowadzi jednoosobową działalność na ryczałcie. Pierwsza dostanie kredyt na
        ok. 827 000 zł. Druga — w zależności od banku — od <strong>276 000 zł do 882 000 zł</strong>.
        Ta sama praca, ten sam dochód, różnica ponad 600 000 zł.
      </p>
      <p className="mt-3">
        Powód jest prosty: <strong>bank nie patrzy na to, ile masz na koncie</strong>. Patrzy na
        dochód wyliczony według własnego algorytmu, a ten algorytm zależy od formy zatrudnienia
        i formy opodatkowania. Ten artykuł tłumaczy, jak liczą banki — i co możesz zrobić na
        rok przed złożeniem wniosku.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Cztery światy dochodu</h2>
      <p>
        Z perspektywy banku nie istnieje jedno „ile zarabiasz". Istnieją cztery osobne
        kategorie, każda z własnymi wymaganiami dokumentowymi i własnym poziomem ryzyka:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Umowa o pracę</strong> — punkt odniesienia, najprostsza ścieżka</li>
        <li><strong>Umowa zlecenie i o dzieło</strong> — akceptowane, ale wymagają udowodnienia ciągłości</li>
        <li><strong>Działalność gospodarcza (JDG)</strong> — dochód liczony z dokumentacji podatkowej, metoda zależy od formy opodatkowania</li>
        <li><strong>Kontrakt B2B</strong> — formalnie działalność gospodarcza, ale bank ocenia też zależność od jednego zleceniodawcy</li>
      </ul>
      <p className="mt-3">
        Kluczowa zasada, która obowiązuje we wszystkich czterech: bank <strong>uśrednia</strong> dochód
        z okresu referencyjnego (6, 12 lub 24 miesiące) i przy tendencji spadkowej bierze
        wartość niższą, nie średnią. To pierwszy element, który zaskakuje przedsiębiorców.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Umowa o pracę — punkt odniesienia</h2>
      <p>
        Etat na czas nieokreślony to najłatwiejszy wariant. Standardowo bank wymaga
        3 miesięcy stażu u obecnego pracodawcy (czasem 6, jeśli wcześniej była przerwa
        w zatrudnieniu) i zaświadczenia o zarobkach z ostatnich 3 miesięcy.
      </p>
      <p className="mt-3">
        Umowa na <strong>czas określony</strong> to już inna sytuacja. Bank sprawdza, ile
        zostało do końca umowy — zwykle musi to być minimum 6, a często 12 miesięcy
        <em>po</em> planowanej dacie uruchomienia kredytu. Umowa kończąca się za 3 miesiące
        praktycznie uniemożliwia uzyskanie kredytu, nawet przy wysokich zarobkach.
      </p>
      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Co obniża dochód liczony przez bank przy etacie</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Premie uznaniowe — część banków nie wlicza ich wcale, część uśrednia z 12 miesięcy</li>
          <li>Nadgodziny i dodatki — traktowane jako dochód zmienny, wymagają dłuższej historii</li>
          <li>Okres wypowiedzenia — jeśli jesteś w trakcie, wniosek zostanie odrzucony</li>
          <li>Zmiana pracodawcy tuż przed wnioskiem — resetuje staż</li>
        </ul>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Umowa zlecenie i o dzieło — liczy się ciągłość</h2>
      <p>
        Banki traktują zlecenie jako dochód z niestabilnego źródła, ale <strong>nie
        odrzucają go automatycznie</strong>. Warunkiem jest udowodnienie, że współpraca
        jest powtarzalna, a nie jednorazowa. Typowe wymagania:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>6–12 miesięcy historii</strong> u tego samego zleceniodawcy (część banków wymaga 24)</li>
        <li><strong>Regularne wpływy na konto</strong> — wyciągi za cały okres referencyjny</li>
        <li><strong>Odprowadzane składki ZUS</strong> — zlecenie bez ZUS jest oceniane znacznie gorzej</li>
        <li><strong>Zaświadczenie od zleceniodawcy</strong> o okresie współpracy i deklaracji jej kontynuacji</li>
      </ul>
      <p className="mt-3">
        Ważne zastrzeżenie: część banków <strong>nie akceptuje zlecenia jako jedynego
        źródła dochodu</strong> — wymaga równolegle etatu lub innego stałego przychodu.
        Inne akceptują je samodzielnie. To nie jest kwestia negocjacji, tylko wewnętrznej
        polityki, dlatego przy zleceniu porównanie ofert w kilku bankach jest obowiązkowe.
      </p>
      <p className="mt-3">
        Umowa <strong>o dzieło</strong> jest najtrudniejsza — brak składek ZUS i jednorazowy
        charakter powodują, że wiele banków wlicza ją jedynie jako dochód uzupełniający,
        a nie podstawowy.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Działalność gospodarcza — tu zaczynają się schody</h2>
      <p>
        Przy JDG bank potrzebuje zwykle <strong>12 lub 24 miesięcy</strong> prowadzenia
        działalności oraz kompletu dokumentów: PIT za poprzedni rok, księgi przychodów
        i rozchodów (lub ewidencji przychodów) za rok bieżący, zaświadczeń o niezaleganiu
        z ZUS i US oraz wyciągów z konta firmowego.
      </p>
      <p className="mt-3">
        Sposób wyliczenia dochodu zależy od <strong>formy opodatkowania</strong> — i to jest
        moment, w którym większość przedsiębiorców traci zdolność kredytową, nie wiedząc
        nawet, że coś stracili.
      </p>

      <h3 className="text-xl font-bold text-foreground mt-6 mb-3">Skala podatkowa i podatek liniowy</h3>
      <p>
        Tu jest stosunkowo uczciwie: bank bierze <strong>dochód</strong> z dokumentacji
        (przychód minus koszty), odejmuje podatek i składki, uśrednia z okresu
        referencyjnego. Problem polega na tym, że każda optymalizacja podatkowa działa
        przeciwko Tobie:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Jednorazowe zakupy</strong> — nowy samochód czy sprzęt wrzucony w koszty obniża dochód dokładnie w okresie, który bada bank</li>
        <li><strong>Amortyzacja</strong> — obniża dochód podatkowy, choć nie jest realnym wydatkiem gotówkowym. Część banków dolicza ją z powrotem do dochodu, część nie — warto o to zapytać</li>
        <li><strong>Strata w którymkolwiek miesiącu</strong> — potrafi wyzerować średnią lub skłonić bank do przyjęcia wartości z gorszego roku</li>
      </ul>

      <h3 className="text-xl font-bold text-foreground mt-6 mb-3">Ryczałt ewidencjonowany — pułapka wskaźnika</h3>
      <p>
        Przy ryczałcie bank <strong>nie widzi Twoich kosztów</strong> — ewidencja przychodów
        ich po prostu nie zawiera. Nie mając danych, bank stosuje własny <strong>wskaźnik
        korygujący</strong>: zakłada, jaki procent przychodu stanowi realny dochód.
      </p>
      <div className="bg-muted/50 rounded-xl p-4 mt-3 text-center font-semibold text-lg">
        Dochód dla banku = przychód × wskaźnik korygujący banku
      </div>
      <p className="mt-3">
        I tu tkwi sedno problemu: <strong>wskaźnik ten waha się mniej więcej od 25% do 90%</strong> w
        zależności od banku, stawki ryczałtu i branży (kodu PKD). Ta sama firma, ten sam
        przychód, ta sama dokumentacja — a wyliczony dochód różni się nawet trzykrotnie.
        Niektóre banki dodatkowo aktualizują te wskaźniki w trakcie roku, różnicując je
        według zawodu przedsiębiorcy.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Ile to realnie kosztuje — konkretne liczby</h2>
      <p>
        Porównajmy dwie osoby, którym na koncie zostaje tyle samo. Założenia:
        oprocentowanie 5,85% (WIBOR ok. 3,85% + marża 2,0%), okres 25 lat, rata maksymalnie
        35% dochodu liczonego przez bank.
      </p>
      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Anna — umowa o pracę, 15 000 zł netto</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Dochód dla banku: <strong>15 000 zł</strong></li>
          <li>Maksymalna rata: 5 250 zł</li>
          <li>Kwota kredytu: <strong>ok. 827 000 zł</strong></li>
        </ul>
      </div>
      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Marek — JDG na ryczałcie, przychód 20 000 zł (na konto zostaje ok. 15 000 zł)</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Bank ze wskaźnikiem 25% → dochód 5 000 zł → rata 1 750 zł → kredyt <strong>ok. 276 000 zł</strong></li>
          <li>Bank ze wskaźnikiem 50% → dochód 10 000 zł → rata 3 500 zł → kredyt <strong>ok. 551 000 zł</strong></li>
          <li>Bank ze wskaźnikiem 80% → dochód 16 000 zł → rata 5 600 zł → kredyt <strong>ok. 882 000 zł</strong></li>
        </ul>
      </div>
      <p className="mt-3">
        Marek nie zmienił niczego w swojej firmie. Zmienił tylko okienko, w którym złożył
        wniosek — i rozstrzał wyniósł <strong>ponad 600 000 zł</strong>. To najważniejszy
        praktyczny wniosek całego artykułu: przy ryczałcie wybór banku ma większe znaczenie
        niż{' '}
        <BlogLink slug="najczestsze-bledy-kredyt-hipoteczny">jakikolwiek inny parametr</BlogLink> kredytu,
        łącznie z marżą.
      </p>

      <div className="mt-4">
        <CalculatorLink loanYears={25} wiborRate={3.85} bankMargin={2.0} section="affordability">
          Sprawdź swoją zdolność przy oprocentowaniu 5,85%
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kontrakt B2B — wysoki dochód, wyższe wymagania</h2>
      <p>
        B2B to formalnie działalność gospodarcza, więc obowiązują wszystkie reguły z
        poprzedniej sekcji. Bank dokłada jednak własną ocenę ryzyka specyficzną dla
        kontraktów:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Staż zwykle krótszy niż przy klasycznej JDG</strong> — przy B2B wiele banków akceptuje już 6 miesięcy, podczas gdy przy działalności handlowej czy usługowej wymaga 12–24</li>
        <li><strong>Zależność od jednego zleceniodawcy</strong> — 100% przychodu od jednego klienta bank czyta jako ryzyko, zbliżone do umowy o pracę bez ochrony kodeksu pracy</li>
        <li><strong>Okres wypowiedzenia w kontrakcie</strong> — 1 miesiąc oceniany jest gorzej niż 3 miesiące</li>
        <li><strong>Umowa na czas nieokreślony</strong> jest wyraźnie lepiej punktowana niż kontrakt kończący się za pół roku</li>
        <li><strong>Poprzedni etat u tego samego klienta</strong> — przejście z etatu na B2B w tej samej firmie część banków traktuje jako kontynuację zatrudnienia i zalicza wcześniejszy staż</li>
      </ul>
      <p className="mt-3">
        Ten ostatni punkt jest zaskakująco często pomijany. Jeśli planujesz przejście na
        B2B u dotychczasowego pracodawcy i kredyt w perspektywie roku — zapytaj o to
        wprost, zanim zaczniesz kompletować dokumenty.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Bufor bezpieczeństwa — dodatkowa warstwa</h2>
      <p>
        Niezależnie od formy zatrudnienia bank nie liczy raty przy dzisiejszym
        oprocentowaniu. Zgodnie z Rekomendacją S Komisji Nadzoru Finansowego musi sprawdzić,
        czy poradzisz sobie także wtedy, gdy stopy wzrosną — dolicza do oprocentowania
        <strong> bufor bezpieczeństwa</strong> (minimum 2,5 punktu procentowego, przy
        oprocentowaniu zmiennym odpowiednio wyższy).
      </p>
      <p className="mt-3">
        W praktyce oznacza to, że przy realnym oprocentowaniu 5,85% zdolność liczona jest
        tak, jakby kredyt kosztował 8,35% lub więcej. To dlatego wyliczenia z kalkulatora
        bywają wyższe niż decyzja banku — kalkulator pokazuje ratę, którą faktycznie
        zapłacisz, bank testuje scenariusz pesymistyczny. Ten sam mechanizm sprawia, że
        wybór{' '}
        <BlogLink slug="stale-czy-zmienne-oprocentowanie">stałego oprocentowania</BlogLink> {' '}
        może podnieść dostępną kwotę kredytu — bufor przy stałej stopie jest niższy.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co zrobić na 12 miesięcy przed wnioskiem</h2>
      <p>
        Zdolności kredytowej przedsiębiorcy nie da się poprawić w tydzień — okres
        referencyjny obejmuje 12 lub 24 miesiące wstecz. Dlatego przygotowanie zaczyna
        się rok wcześniej:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Wstrzymaj duże zakupy w koszty</strong> — nowy samochód czy sprzęt kupiony w okresie referencyjnym bezpośrednio obniża dochód widziany przez bank (dotyczy skali i liniowego)</li>
        <li><strong>Rozważ, czy optymalizacja podatkowa nadal się opłaca</strong> — kilka tysięcy złotych zaoszczędzone na podatku może kosztować sto tysięcy zdolności kredytowej</li>
        <li><strong>Zadbaj o brak zaległości w ZUS i US</strong> — zaświadczenia o niezaleganiu są obowiązkowe, a jedna zaległość potrafi zablokować wniosek</li>
        <li><strong>Nie zmieniaj formy opodatkowania tuż przed wnioskiem</strong> — bank będzie miał niepełny obraz i przyjmie wariant ostrożniejszy</li>
        <li><strong>Zamknij niepotrzebne karty kredytowe i limity</strong> — bank liczy cały limit jako zobowiązanie, nawet jeśli go nie używasz</li>
        <li><strong>Dbaj o regularne wpływy firmowe</strong> — sezonowość i przestoje obniżają uśredniony dochód</li>
        <li><strong>Zbieraj <BlogLink slug="wklad-wlasny-kredyt-hipoteczny">wkład własny</BlogLink></strong> — przy nietypowej formie dochodu wyższy wkład realnie poprawia decyzję banku</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Praktyczna kolejność działań</h2>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Policz zdolność samodzielnie</strong> — zacznij od{' '}
          <BlogLink slug="jak-obliczyc-zdolnosc-kredytowa">własnego wyliczenia</BlogLink>, żeby wiedzieć,
          o jakiej kwocie rozmawiasz</li>
        <li><strong>Sprawdź, jak bank policzy Twój dochód</strong> — przy ryczałcie zapytaj wprost
          o wskaźnik korygujący dla Twojego PKD, jeszcze przed złożeniem wniosku</li>
        <li><strong>Porównaj minimum 4–5 banków</strong> — przy etacie różnice są kosmetyczne, przy
          działalności decydują o dostępności kredytu w ogóle</li>
        <li><strong>Porównuj oferty po <BlogLink slug="rrso-kredyt-hipoteczny">RRSO</BlogLink></strong>,
          a nie po samym oprocentowaniu — dopiero gdy masz już kilka pozytywnych decyzji</li>
        <li><strong>Rozważ współkredytobiorcę na etacie</strong> — partner z umową o pracę stabilizuje
          ocenę całego wniosku</li>
      </ul>

      <div className="mt-4">
        <CalculatorLink propertyPrice={690000} downPaymentPercent={20} loanYears={25} wiborRate={3.85} bankMargin={2.0}>
          Policz ratę dla mieszkania za 690 000 zł
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Podsumowanie</h2>
      <p>
        Forma zatrudnienia nie zamyka drogi do kredytu hipotecznego — zmienia natomiast
        sposób, w jaki bank czyta Twoje pieniądze. Na etacie liczy się to, co widzi
        w zaświadczeniu. Na zleceniu — ciągłość współpracy i składki ZUS. Na działalności
        — dokumentacja podatkowa, a przy ryczałcie wręcz arbitralny wskaźnik korygujący,
        który potrafi zmienić dostępną kwotę kredytu o kilkaset tysięcy złotych.
      </p>
      <p className="mt-3">
        Dwa wnioski praktyczne. Po pierwsze: <strong>przygotowanie zaczyna się rok przed
        wnioskiem</strong>, bo bank patrzy wstecz, nie w przód. Po drugie: przy dochodzie
        innym niż etat <strong>wybór banku jest ważniejszy od negocjacji marży</strong> —
        różnica w metodzie liczenia dochodu przebija każdą różnicę w cenie kredytu.
      </p>
      <p className="mt-3">
        Zanim zaczniesz rozmowy z bankami, warto wiedzieć,{' '}
        <BlogLink slug="ile-zarabiac-na-kredyt-hipoteczny">jakiego dochodu wymaga</BlogLink> {' '}
        kwota, o której myślisz — a potem sprawdzić w kalkulatorze, jak zmienia się rata
        przy różnych scenariuszach.
      </p>

      <div className="mt-4">
        <CalculatorLink section="affordability">
          Sprawdź swoją ratę w kalkulatorze RatyGo
        </CalculatorLink>
      </div>
    </>
  )
}
